import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { AppConfig } from '~/core/models/appconfig';

/**
 * Config file paths to try in order:
 *  1. browser/assets/config.json  – written by the Ansible deploy role at deploy time (contains real env values)
 *  2. assets/config.json          – used during local `ng serve` (hardcoded localhost values)
 */
const FILES = ['browser/assets/config.json', 'assets/config.json'];

/**
 * Optional per-developer local override file (gitignored).
 * Create src/assets/setlocal.json to override individual keys without touching committed files.
 */
const LOCAL_FILE = 'assets/setlocal.json';

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    private config: AppConfig = new AppConfig();
    private http: HttpClient;

    constructor(private httpHandler: HttpBackend) {
        this.http = new HttpClient(this.httpHandler);
    }

    public async init(): Promise<boolean> {
        try {
            let configData: AppConfig | null = null;

            // Try each config file path until one succeeds
            for (const file of FILES) {
                try {
                    configData = await lastValueFrom(this.http.get<AppConfig>(file));
                    break;
                } catch (e) {
                    console.warn(`Failed to load config from ${file}, trying next...`);
                }
            }

            if (!configData) {
                throw new Error('Failed to load config from any path');
            }

            // Optionally merge setlocal.json (per-developer local overrides, gitignored)
            let localConfig: Partial<AppConfig> | null = null;
            try {
                localConfig = await lastValueFrom(this.http.get<Partial<AppConfig>>(LOCAL_FILE));
                console.log('Loaded local config override:', localConfig);
            } catch (e) {
                // setlocal.json is optional — silently skip if not present
            }

            this.config = { ...configData, ...(localConfig || {}) };
            return true;
        } catch (error) {
            console.error('Error loading config:', error);
            throw error;
        }
    }

    public getConfig(key?: string | Array<string>): any {
        if (!key || (Array.isArray(key) && !key[0])) {
            return this.config;
        }

        if (!Array.isArray(key)) {
            key = key.split('.');
        }

        return key.reduce((acc: any, current: string) => acc?.[current], this.config);
    }
}